import React, { useState, useRef, useEffect } from 'react';
import { 
    FaChalkboardTeacher, FaPen, FaEraser, FaTrash, FaTimes 
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import PageLayout from '../components/PageLayout';

// --- COMPONENT: POP-OUT WHITEBOARD ---
const Whiteboard = ({ onClose }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState('#000000');
    const [lineWidth, setLineWidth] = useState(3);
    const [tool, setTool] = useState('pen'); 

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;

        const handleResize = () => {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            ctx.putImageData(imageData, 0, 0);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
        ctx.lineWidth = tool === 'eraser' ? 20 : 3;
    }, [color, tool]);

    const startDrawing = ({ nativeEvent }) => {
        const { offsetX, offsetY } = getCoordinates(nativeEvent);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    };

    const draw = ({ nativeEvent }) => {
        if (!isDrawing) return;
        const { offsetX, offsetY } = getCoordinates(nativeEvent);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(offsetX, offsetY);
        ctx.stroke();
    };

    const stopDrawing = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.closePath();
        setIsDrawing(false);
    };

    const getCoordinates = (event) => {
        if (event.touches && event.touches[0]) {
            const rect = canvasRef.current.getBoundingClientRect();
            return {
                offsetX: event.touches[0].clientX - rect.left,
                offsetY: event.touches[0].clientY - rect.top
            };
        }
        return { offsetX: event.offsetX, offsetY: event.offsetY };
    };

    const clearCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-white animate-fade-in touch-none">
            <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="cursor-crosshair w-full h-full block"
            />
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-6 border border-gray-700">
                <button onClick={onClose} className="bg-red-500 hover:bg-red-600 p-2 rounded-full transition-colors"><FaTimes /></button>
                <div className="w-px h-8 bg-gray-600 mx-2"></div>
                <button onClick={() => setTool('pen')} className={`p-2 rounded-lg transition ${tool === 'pen' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}><FaPen /></button>
                <div className="flex gap-2">
                    {['#000000', '#EF4444', '#3B82F6', '#10B981'].map((c) => (
                        <button key={c} onClick={() => { setColor(c); setTool('pen'); }} className={`w-6 h-6 rounded-full border-2 ${color === c && tool === 'pen' ? 'border-white scale-125' : 'border-transparent hover:scale-110'} transition-transform`} style={{ backgroundColor: c }} />
                    ))}
                </div>
                <button onClick={() => setTool('eraser')} className={`p-2 rounded-lg transition ${tool === 'eraser' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}><FaEraser /></button>
                <button onClick={clearCanvas} className="text-gray-400 hover:text-red-400 transition ml-2"><FaTrash /></button>
            </div>
        </div>
    );
};

const LessonLibrary = () => {
    const { user } = useAuth();
    const { navigateTo } = useNavigation();
    const [showWhiteboard, setShowWhiteboard] = useState(false);

    // SECURITY CHECK: If student, kick them out
    if (user && user.role === 'student') {
        setTimeout(() => navigateTo('dashboard'), 100);
        return null;
    }

    return (
        <PageLayout>
            {showWhiteboard && <Whiteboard onClose={() => setShowWhiteboard(false)} />}
            <div className="max-w-4xl mx-auto pt-10 px-4">
                
                <div className="mb-12 text-center">
                    <h1 className="text-4xl font-black text-gray-800 flex justify-center items-center gap-3">
                        <FaChalkboardTeacher className="text-blue-600"/> Teacher Tools
                    </h1>
                    <p className="text-gray-500 font-medium mt-2">Tools for your live classes</p>
                </div>

                {/* THE ONLY TOOL: WHITEBOARD */}
                <div 
                    onClick={() => setShowWhiteboard(true)}
                    className="bg-gradient-to-br from-indigo-500 to-purple-600 p-10 rounded-3xl shadow-2xl cursor-pointer transform hover:-translate-y-2 transition-all text-white flex flex-col items-center justify-center text-center group"
                >
                    <div className="bg-white/20 p-4 rounded-full mb-6 group-hover:scale-110 transition-transform">
                        <FaChalkboardTeacher size={48} className="text-white" />
                    </div>
                    <h3 className="font-bold text-3xl mb-2">Open Whiteboard</h3>
                    <p className="text-indigo-100 text-lg mb-6 max-w-md">
                        Launch the full-screen interactive canvas for drawing and explaining concepts.
                    </p>
                    <button className="bg-white text-indigo-600 px-8 py-3 rounded-full font-black text-lg shadow-lg hover:bg-indigo-50 transition-colors">
                        Launch Now
                    </button>
                </div>

            </div>
        </PageLayout>
    );
};

export default LessonLibrary;